import {timestamp} from './request.utils'
import type {AdvertiseTranslatable} from '../models'
import moment, {Moment} from '../utils/momentLocalized'
import {langLocales} from './langLocales'

export type Translation = (key: string, options?: any) => string

export type DateTimeFormat = 'date' | 'time' | 'datetime' | 'longdate'

export type DateTimeFormats = Record<DateTimeFormat, string>

export const DEFAULT_DATE_TIME_FORMATS: DateTimeFormats = {
  date: 'DD.MM.YYYY',
  time: 'HH:mm',
  datetime: 'DD.MM.YYYY HH:mm',
  longdate: 'DD MMMM, YYYY',
}

export interface CommonUtils {
  readonly localizedDateTimeFormats: DateTimeFormats
  timestamp(): number
  getKmFromMeters(meters: number): number
  shortName(fullName: string): string
  sinceDate(date: number, lang: string): string
  isTheSameDay(first: number, second: number): boolean
  isTheSameBeforeDay(first: number, second: number): boolean
  draftListItemPublishDate(t: Translation, time: number, lang: string): string
  draftListItemExpiresIn(t: Translation, time: number): string
  productPublishDate(t: Translation, time: number, lang: string): string
  // used for swap title in mobx model
  swapTitle(advertise: AdvertiseTranslatable): AdvertiseTranslatable
  // used for swap description in mobx model
  swapDescription(advertise: AdvertiseTranslatable): AdvertiseTranslatable
  getFormatted: (date: Moment, format: DateTimeFormat, lang: string) => string
  getCalendarDateTime: (date: Moment, lang: string) => string
}

export class CommonUtilsImplementation implements CommonUtils {
  get localizedDateTimeFormats(): DateTimeFormats {
    // TODO Will fetches from a backend in the future
    return DEFAULT_DATE_TIME_FORMATS
  }

  getFormatted = (date: Moment, format: DateTimeFormat, lang: string) =>
    date
      .locale(this.getLocaleLang(lang))
      .format(this.localizedDateTimeFormats[format])

  getCalendarDateTime = (date: Moment, lang: string) => {
    const localizedDate = date.locale(this.getLocaleLang(lang))
    return localizedDate.calendar(localizedDate, {
      sameDay: this.localizedDateTimeFormats.datetime,
      sameElse: this.localizedDateTimeFormats.date,
      lastWeek: this.localizedDateTimeFormats.date,
    })
  }

  timestamp = () => timestamp()
  getKmFromMeters = (meters: number) => meters * 1000

  shortName(fullName: string): string {
    let name = ''
    const parts = fullName.split(' ')
    name += parts[0].charAt(0)
    if (parts.length > 1) name += parts[1].charAt(0)
    return name.toUpperCase()
  }

  sinceDate = (date: number, lang: string): string => {
    return this.getFormatted(moment.unix(date ?? 0), 'date', lang)
  }

  swapTitle = (advertise: AdvertiseTranslatable) => {
    if (advertise.title !== advertise.titleOriginal) {
      let title = advertise.titleOriginal
      advertise.titleOriginal = advertise.title
      advertise.title = title
      advertise.isTranslated =
        advertise.isTranslated === undefined ? true : !advertise.isTranslated
    } else {
      let title = advertise.title
      advertise.title = advertise.titleOriginal
      advertise.titleOriginal = title
      advertise.isTranslated = false
    }
    return advertise
  }

  swapDescription = (advertise: AdvertiseTranslatable) => {
    if (advertise.description !== advertise.descriptionOriginal) {
      let description = advertise.descriptionOriginal
      advertise.descriptionOriginal = advertise.description
      advertise.description = description
    } else {
      let description = advertise.description
      advertise.description = advertise.descriptionOriginal
      advertise.descriptionOriginal = description
    }
    return advertise
  }

  isTheSameDay = (first: number, second: number) => {
    return moment.unix(first).isSame(moment(second), 'day')
  }

  isTheSameBeforeDay = (first: number, second: number) => {
    return moment.unix(first).isSame(moment(second).subtract(1, 'day'), 'day')
  }

  productPublishDate = (t: Translation, time: number, lang: string) => {
    const today = new Date().getTime()
    if (this.isTheSameDay(time, today))
      return t('TODAY_AT', {
        date: this.getFormatted(moment.unix(time), 'time', lang),
      })
    if (this.isTheSameBeforeDay(time, today))
      return t('YESTERDAY_AT', {
        date: this.getFormatted(moment.unix(time), 'time', lang),
      })
    return this.getFormatted(moment.unix(time), 'datetime', lang)
  }

  draftListItemPublishDate = (t: Translation, time: number, lang: string) => {
    return this.productPublishDate(t, time, lang)
  }

  draftListItemExpiresIn = (t: Translation, time: number) => {
    const diff = Math.abs(
      moment().add(-30, 'days').diff(moment.unix(time), 'days'),
    )
    return t(`NUMBER_OF_DAYS_TO_DELETE_DRAFT`, {
      count: diff,
    })
  }

  private getLocaleLang = (lang: string) => langLocales.get(lang) ?? 'en-gb'
}
